import classes from "./ContextSidebar.module.css";
import {
    ActionIcon,
    Alert,
    Box,
    Loader,
    Text,
    Tooltip,
    Stack,
} from "@mantine/core";
import {useState, useCallback, useEffect, useRef} from "react";
import {RelatedToolsList} from "./RelatedToolsList";
import {RelatedContentList} from "./RelatedContentList";
import {EditableTitle} from "./EditableTitle";
import {chatApi} from "../../chatApi";
import IconArrowRight from "../../../../assets/arrow-right.svg?react";
import IconArrowLeft from "../../../../assets/arrow-left.svg?react";
import IconAlertCircle from "../../../../assets/alert-circle.svg?react";
import {t} from "i18next";
import {useAppSelector} from "../../../../app/hooks";
import {useMediaQuery, readLocalStorageValue} from "@mantine/hooks";
import {
    SidebarPosition,
    DEFAULT_SIDEBAR_POSITION,
} from "../../hooks/useSidebarPosition";
import {LocalStorageKeys} from "../../../../types";

type ContextSidebarProps = {
    title: string;
    searchQuery?: string;
    position?: SidebarPosition;
    navbarWidth?: number;
};

const MIN_WIDTH = 300;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 420;
// Track collapsed threads for the current session only (clears when tab closes)
const collapsedThreads = new Set<string>();

const getCollapsedState = (threadId: string | null): boolean => {
    if (!threadId) return false; // Open by default for new threads
    return collapsedThreads.has(threadId);
};

const setCollapsedState = (threadId: string | null, collapsed: boolean) => {
    if (!threadId) return;
    if (collapsed) {
        collapsedThreads.add(threadId);
    } else {
        collapsedThreads.delete(threadId);
    }
};

export const ContextSidebar = ({
    title,
    searchQuery,
    position = DEFAULT_SIDEBAR_POSITION,
    navbarWidth = 0,
}: ContextSidebarProps) => {
    const threadId = useAppSelector((state) => state.chat.threadId);
    const isDesktop = useMediaQuery("(min-width: 48em)", undefined, {
        getInitialValueInEffect: false,
    });
    const showInternalFeatures = readLocalStorageValue({
        key: LocalStorageKeys.ShowInternalFeatures,
        defaultValue: false,
    });
    const showDebugIndicators = readLocalStorageValue({
        key: LocalStorageKeys.ShowDebugIndicators,
        defaultValue: false,
    });
    const [width, setWidth] = useState(DEFAULT_WIDTH);
    const [isResizing, setIsResizing] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(() => {
        // On mobile, always start collapsed
        if (!window.matchMedia("(min-width: 48em)").matches) {
            return true;
        }
        return getCollapsedState(threadId);
    });
    const sidebarRef = useRef<HTMLDivElement>(null);
    const rafRef = useRef<number | null>(null);

    // Use position from prop (managed by Thread component)
    const currentPosition = position || DEFAULT_SIDEBAR_POSITION;

    // Collapse sidebar when switching to mobile
    useEffect(() => {
        if (isDesktop === false) {
            setIsCollapsed(true);
            setCollapsedState(threadId, true);
        }
    }, [isDesktop]);

    // Fetch sidebar search results based on the first user message
    const {
        data: searchResults,
        isLoading,
        isError,
    } = chatApi.endpoints.sidebarSearch.useQuery(
        {
            query: searchQuery || "",
            take: 15,
            systemname: "DIB",
            threadId: threadId,
        },
        {
            skip: !searchQuery || !showInternalFeatures,
        }
    );

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    const handleMouseDblClik = () => setWidth(DEFAULT_WIDTH);

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isResizing) return;

            // Cancel any pending animation frame to avoid stacking
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }

            // Use requestAnimationFrame for performance throttling
            rafRef.current = requestAnimationFrame(() => {
                let newWidth: number;
                if (currentPosition === SidebarPosition.Left) {
                    // When on left, resize from right edge - account for navbar offset
                    newWidth = e.clientX - navbarWidth;
                } else {
                    // When on right, resize from left edge
                    newWidth = window.innerWidth - e.clientX;
                }

                if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
                    setWidth(newWidth);
                }
                rafRef.current = null;
            });
        },
        [isResizing, currentPosition, navbarWidth]
    );

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    const toggleCollapsed = useCallback(() => {
        setIsCollapsed((prev) => {
            const newValue = !prev;
            setCollapsedState(threadId, newValue);
            return newValue;
        });
    }, [threadId]);

    // Sync collapsed state when switching threads
    useEffect(() => {
        setIsCollapsed(getCollapsedState(threadId));
    }, [threadId]);

    useEffect(() => {
        if (isResizing) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";
        }

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
            // Clean up any pending animation frame
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    const mobileWidth = "100vw";
    const effectiveWidth = isDesktop ? width : mobileWidth;

    if (!showInternalFeatures) return null;

    return (
        <Box
            ref={sidebarRef}
            className={`${classes.contextSidebar} ${
                isCollapsed ? classes.collapsed : ""
            } ${isResizing ? classes.resizing : ""} ${
                currentPosition === SidebarPosition.Left
                    ? classes.positionLeft
                    : ""
            } ${!isDesktop ? classes.mobile : ""}`}
            style={
                isCollapsed
                    ? {width: 0, flexBasis: 0}
                    : {width: effectiveWidth, flexBasis: effectiveWidth}
            }
        >
            {!isCollapsed && isDesktop && (
                <div
                    className={classes.resizeHandle}
                    onMouseDown={handleMouseDown}
                    onDoubleClick={handleMouseDblClik}
                />
            )}

            <Tooltip
                label={
                    isCollapsed
                        ? t("contextSidebar.openContextSidebar")
                        : t("contextSidebar.closeContextSidebar")
                }
                position={
                    currentPosition === SidebarPosition.Left ? "right" : "left"
                }
            >
                <ActionIcon
                    className={classes.toggleButton}
                    variant="subtle"
                    color="gray"
                    size="sm"
                    onClick={toggleCollapsed}
                    aria-label={
                        isCollapsed
                            ? t("contextSidebar.openContextSidebar")
                            : t("contextSidebar.closeContextSidebar")
                    }
                >
                    {isCollapsed ===
                    (currentPosition === SidebarPosition.Left) ? (
                        <IconArrowRight />
                    ) : (
                        <IconArrowLeft />
                    )}
                </ActionIcon>
            </Tooltip>

            <Box
                className={classes.contentWrapper}
                style={{
                    width: effectiveWidth,
                    minWidth: isDesktop ? width : "100%",
                }}
            >
                <Box className={classes.titleSection}>
                    <Stack gap={4}>
                        <Text size="10px" tt="uppercase" c="dimmed" mb={4}>
                            {t("contextSidebar.ThreadExplanation")}
                        </Text>
                        <EditableTitle title={title} threadId={threadId} />
                    </Stack>
                </Box>

                {isLoading && <Loader size="sm" color="gneis" />}

                {isError && (
                    <Alert
                        icon={<IconAlertCircle />}
                        color="red"
                        variant="light"
                    >
                        <Text size="sm">{t("contextSidebar.error")}</Text>
                    </Alert>
                )}

                {!isLoading && !isError && searchQuery && (
                    <Stack gap="xl">
                        <RelatedToolsList
                            results={searchResults?.tools ?? []}
                            showDebugIndicators={showDebugIndicators}
                        />
                        <RelatedContentList
                            results={searchResults?.otherSources ?? []}
                            showDebugIndicators={showDebugIndicators}
                        />
                        {showDebugIndicators && searchResults?.data && (
                            <Stack
                                gap="xs"
                                style={{
                                    padding: "var(--mantine-spacing-xs)",
                                    border: "1px dashed var(--mantine-color-grape-5)",
                                    borderRadius: "var(--mantine-radius-sm)",
                                }}
                            >
                                <Text size="xs" fw={600} c="grape">
                                    Debug: Search Metadata
                                </Text>
                                <Text size="xs" ff="monospace">
                                    <strong>keywordsGenerated:</strong>{" "}
                                    {searchResults.data.keywordsGenerated}
                                </Text>
                                <Text size="xs" ff="monospace">
                                    <strong>hyDeVector:</strong>{" "}
                                    {searchResults.data.hyDeVector}
                                </Text>
                                <Text size="xs" ff="monospace">
                                    <strong>searchQuery:</strong>{" "}
                                    {searchResults.data.searchQuery}
                                </Text>
                            </Stack>
                        )}
                    </Stack>
                )}
            </Box>
        </Box>
    );
};
